import React, {useState, useRef, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {defaultTheme} from '@stencil/build-theme';
import HappyCloseIcon from '@stencil/icons/src/HappyCloseIcon/HappyCloseIcon';
import HappyCommonSpace from '@stencil/icons/src/HappyCommonSpace/HappyCommonSpace';

import CaptivateImageGridUI from '../../CaptivateImagesGrid/CaptivateImageGridUI';
import * as S from './HomeStayGallery.styles';
import HomeStayGalleryAllImageHeader from './HomeStayGalleryAllImageHeader';
import HomeStayGalleryAllMediaImages from './HomeStayGalleryAllMediaImages';

const HomeStayGallery = ({
  closeModal,
  privateSpaces = [],
  sharedSpaces = [],
  modalSelectedImage = {},
}) => {
  const dispatch = useDispatch();
  const [selectedItem, setSelectedItem] = useState(0);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const timeOutRef = useRef(null);
  const galleryRefs = useRef([]);
  const navClicked = useRef(false);
  const wrapperRef = useRef(null);
  const roomList = useMemo(() => {
    const safePrivateSpaces = privateSpaces || [];
    const safeSharedSpaces = sharedSpaces || [];
    return [...safePrivateSpaces, ...safeSharedSpaces].map(roomItem => ({
      ...roomItem,
      media: (roomItem.media || []).map((mediaItem, idx) => ({
        ...mediaItem,
        imageNo: idx + 1,
        gallery_url: mediaItem?.url || '',
      })),
    }));
  }, [privateSpaces, sharedSpaces]);

  const handleScroll = () => {
    if (navClicked.current === true) {
      // navClicked.current = false;
      return;
    }

    const currentScrollY = wrapperRef?.current?.scrollTop;

    for (let i = 0; i <= galleryRefs.current.length; i++) {
      let currentItem = galleryRefs.current[i];
      let nextItem = galleryRefs.current[i + 1];
      let lastItem = galleryRefs.current[galleryRefs.current.length - 1];
      let currentItemOffset = currentItem?.offsetTop;

      let nextItemOffset = nextItem?.offsetTop;
      let lastItemOffset = lastItem?.offsetTop;

      if (currentScrollY > currentItemOffset && currentScrollY <= nextItemOffset) {
        setSelectedItem(i);
        // continue;
      } else if (currentScrollY > lastItemOffset) {
        setSelectedItem(galleryRefs.current.length - 1);
      }
    }
  };

  const onItemSelect = (item, itemId) => {
    dispatch(
      omnitureThunk(omnitureConfig.pages.Details, omnitureConfig.events.ctaClick, {
        cta: {
          name: `PropertyLayoutModal_${item?.name || ''}_clicked`,
        },
      }),
    );
    setSelectedRoom(null);
    setSelectedItem(itemId);
    clearTimeout(timeOutRef.current);
    navClicked.current = true;
    galleryRefs.current[itemId]?.scrollIntoView({behavior: 'smooth'});
    timeOutRef.current = setTimeout(() => {
      navClicked.current = false;
    }, 1000);
  };

  const hamdlerSelectedRoomImage = (roomObj, imageNo) => {
    setSelectedRoom({...roomObj, selectedImageNo: imageNo});
    wrapperRef?.current?.scrollIntoView({behavior: 'smooth'});
    dispatch(
      omnitureThunk(omnitureConfig.pages.Details, omnitureConfig.events.ctaClick, {
        cta: {
          name: `PropertyLayoutModal_${roomObj?.name || ''}_ImageClicked`,
        },
      }),
    );
  };

  useEffect(() => {
    if (wrapperRef?.current) {
      wrapperRef?.current?.addEventListener('scroll', handleScroll);
      return () => {
        wrapperRef?.current?.removeEventListener('scroll', handleScroll);
      };
    }
  }, [wrapperRef.current, navClicked.current]);

  useEffect(() => {
    if (roomList?.length && modalSelectedImage?.spaceId) {
      const newIdx = roomList?.findIndex(d => d?.spaceId === modalSelectedImage?.spaceId);
      if (newIdx !== -1) {
        onItemSelect(modalSelectedImage, newIdx);
      }
    }
  }, [roomList]);

  return (
    <>
      <S.ModalOuterWrapper>
        {selectedRoom && Object.keys(selectedRoom).length ? (
          <S.ModalLeftWrapper>
            <HomeStayGalleryAllMediaImages
              setSelectedRoom={setSelectedRoom}
              selectedRoom={selectedRoom}
              imageGridDataListArrays={[selectedRoom?.media]}
              maxImageGridType={8}
              mode="fixed"
            />
          </S.ModalLeftWrapper>
        ) : (
          <S.ModalLeftWrapper ref={wrapperRef}>
            {roomList.map((item, idx) => {
              const defaultGridType = idx % 2 === 0 ? 3 : 4;
              const gridType =
                item?.media?.length < defaultGridType ? item?.media?.length : defaultGridType;
              const dataList = item.media.slice(0, gridType);
              return (
                <div
                  key={`${item?.name}-${idx}-${item?.subText}`}
                  ref={element => {
                    galleryRefs.current[idx] = element;
                  }}
                  className="galleryWrapper gallery1Wrap"
                >
                  <HomeStayGalleryAllImageHeader
                    onCategorySelection={setSelectedRoom}
                    overviewData={item}
                    gridType={gridType}
                  />
                  <div className="masonryLayoutWrapper">
                    <CaptivateImageGridUI
                      type={gridType}
                      dataList={dataList}
                      updateCarouselState={hamdlerSelectedRoomImage.bind(this, item)}
                      variation={1}
                    />
                  </div>
                </div>
              );
            })}
          </S.ModalLeftWrapper>
        )}
        <S.ModalRightWrapper>
          <S.ModalRightHeaderWrapper>
            <p className="headerText">Property Layout</p>
            <HappyCloseIcon
              height="2rem"
              width="2rem"
              fill={defaultTheme.colors.downriver.base}
              onClick={closeModal}
            ></HappyCloseIcon>
          </S.ModalRightHeaderWrapper>
          <S.ModalRightContentWrapper>
            <S.ModalRightContentList>
              {roomList.map((item, idx) => {
                return (
                  <S.ModalRightContentListItem
                    selected={idx === selectedItem}
                    key={idx}
                    onClick={() => onItemSelect(item, idx)}
                  >
                    <p className="headerText">
                      {item.name}
                      {item.isCommonSpace && <span className="commonTag">Common</span>}
                    </p>
                    <p className="descText">
                      {item.descriptionText
                        ? item.descriptionText
                        : item.spaceInclusions?.join(', ')}
                    </p>
                    {item.commonSpaceDesc && (
                      <p className="commonText">
                        <HappyCommonSpace margin="0 0.7rem 0 0" />
                        {item.commonSpaceDesc}
                      </p>
                    )}
                  </S.ModalRightContentListItem>
                );
              })}
            </S.ModalRightContentList>
          </S.ModalRightContentWrapper>
        </S.ModalRightWrapper>
      </S.ModalOuterWrapper>
    </>
  );
};

export default HomeStayGallery;
